import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  PackageOpen,
  ArrowDownToLine,
  ScanBarcode,
  ClipboardCheck,
  Repeat2,
  Truck,
  RotateCcw,
  ChevronRight,
  PlayCircle,
  UserCheck,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { RfShell, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { useScope } from "@/hooks/use-scope";
import { getMyWork, type MyWorkItem, type MyWorkPayload } from "@/lib/my-work.functions";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/rf/")({
  component: RfHome,
});

const WORKSPACES = [
  { to: "/app/rf/receiving", icon: PackageOpen, label: "Receiving" },
  { to: "/app/rf/putaway", icon: ArrowDownToLine, label: "Putaway" },
  { to: "/app/rf/pick", icon: ScanBarcode, label: "Picking" },
  { to: "/app/rf/replenishment", icon: Repeat2, label: "Replenish" },
  { to: "/app/rf/cycle-count", icon: ClipboardCheck, label: "Counts" },
  { to: "/app/rf/transfers", icon: Truck, label: "Transfers" },
  { to: "/app/rf/returns", icon: RotateCcw, label: "Returns" },
] as const;

function RfHome() {
  const { tenantId, facilityId } = useScope();
  const fn = useServerFn(getMyWork);
  const { data, isLoading } = useQuery({
    queryKey: ["my-work", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => fn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
    refetchInterval: 30_000,
  });
  const [filter, setFilter] = useState<DueFilter>("all");

  if (!tenantId || !facilityId) {
    return (
      <RfShell title="My Work">
        <RfScopeMissing />
        <WorkspacesStrip />
      </RfShell>
    );
  }

  return (
    <RfShell title="My Work">
      <DueFilterChips value={filter} onChange={setFilter} />
      {isLoading || !data ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <MyWorkSections data={data} filter={filter} />
      )}
      <WorkspacesStrip />
      <p className="px-1 pt-1 text-[11px] text-muted-foreground">
        Tap any workspace if a supervisor redirects you or a truck arrives early.
      </p>
    </RfShell>
  );
}

type DueFilter = "all" | "overdue" | "today" | "tomorrow";

function dueBucket(iso: string | null | undefined): DueFilter | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfTomorrow = startOfToday + 86400_000;
  const startOfDayAfter = startOfTomorrow + 86400_000;
  if (t < startOfToday || t < Date.now()) {
    // Anything before today, or already past its cutoff today, is overdue.
    if (t < Date.now() && t >= startOfToday) {
      // Past its cutoff but still calendar-today — treat as overdue, not today.
      return "overdue";
    }
    if (t < startOfToday) return "overdue";
  }
  if (t >= startOfToday && t < startOfTomorrow) return "today";
  if (t >= startOfTomorrow && t < startOfDayAfter) return "tomorrow";
  return null;
}

function DueFilterChips({
  value,
  onChange,
}: {
  value: DueFilter;
  onChange: (v: DueFilter) => void;
}) {
  const opts: { id: DueFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "overdue", label: "Overdue" },
    { id: "today", label: "Today" },
    { id: "tomorrow", label: "Tomorrow" },
  ];
  return (
    <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1">
      {opts.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={cn(
              "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium active:bg-muted/70",
              active
                ? "border-primary bg-primary text-primary-foreground"
                : "bg-card text-foreground",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function MyWorkSections({ data, filter }: { data: MyWorkPayload; filter: DueFilter }) {
  const sections: Array<{
    key: keyof MyWorkPayload;
    title: string;
    empty: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: string;
  }> = [
    { key: "resume", title: "Resume work", empty: "Nothing in progress.", icon: PlayCircle, tone: "text-primary" },
    { key: "assigned", title: "Assigned to me", empty: "No work assigned right now.", icon: UserCheck, tone: "text-primary" },
    { key: "urgent", title: "Urgent", empty: "No cutoffs at risk.", icon: AlertTriangle, tone: "text-destructive" },
    { key: "recommended", title: "Recommended next", empty: "Queue is clear.", icon: Sparkles, tone: "text-muted-foreground" },
  ];
  const filtered = useMemo(() => {
    if (filter === "all") return data;
    const out: MyWorkPayload = { resume: [], assigned: [], recommended: [], urgent: [], attention: [] };
    (Object.keys(out) as (keyof MyWorkPayload)[]).forEach((k) => {
      out[k] = data[k].filter((it) => dueBucket(it.dueAt) === filter);
    });
    return out;
  }, [data, filter]);
  const totalVisible = filtered.resume.length + filtered.assigned.length + filtered.recommended.length + filtered.urgent.length;
  if (filter !== "all" && totalVisible === 0) {
    return (
      <RfEmpty>
        Nothing {filter === "overdue" ? "overdue" : filter === "today" ? "due today" : "due tomorrow"}.
      </RfEmpty>
    );
  }
  return (
    <div className="space-y-4">
      {sections.map((s) => {
        const items = filtered[s.key];
        // In a filtered view, hide sections that are empty — empty-state
        // copy assumes "All" and would be misleading under a date filter.
        if (filter !== "all" && items.length === 0) return null;
        const Icon = s.icon;
        return (
          <section key={s.key} className="space-y-1.5">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Icon className={cn("h-4 w-4", s.tone)} />
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {s.title}
                </h2>
              </div>
              {items.length > 0 ? (
                <span className="text-[11px] text-muted-foreground">{items.length}</span>
              ) : null}
            </div>
            {items.length === 0 ? (
              <RfEmpty>{s.empty}</RfEmpty>
            ) : (
              <ul className="space-y-2">
                {items.map((it) => (
                  <WorkRow key={`${it.kind}-${it.id}`} item={it} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function WorkRow({ item }: { item: MyWorkItem }) {
  const toneClass =
    item.tone === "danger"
      ? "border-destructive/40 bg-destructive/5"
      : item.tone === "warn"
        ? "border-amber-500/40 bg-amber-500/5"
        : item.tone === "success"
          ? "border-emerald-500/40 bg-emerald-500/5"
          : "";
  return (
    <li>
      <Link
        to={item.href as never}
        className={cn(
          "flex items-center gap-3 rounded-md border bg-card px-3 py-3 active:bg-muted/70",
          toneClass,
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{item.primary}</div>
          {item.secondary ? (
            <div className="truncate text-xs text-muted-foreground">{item.secondary}</div>
          ) : null}
          {item.meta ? (
            <div className="text-[11px] text-muted-foreground mt-0.5">{item.meta}</div>
          ) : null}
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
      </Link>
    </li>
  );
}

function WorkspacesStrip() {
  return (
    <section className="space-y-1.5 pt-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Workspaces
        </h2>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {WORKSPACES.map((w) => {
          const Icon = w.icon;
          return (
            <Link
              key={w.to}
              to={w.to as never}
              className="flex flex-col items-center justify-center gap-1.5 rounded-md border bg-card px-2 py-3 text-center active:bg-muted/70"
            >
              <Icon className="h-5 w-5 text-primary" />
              <span className="text-xs font-medium">{w.label}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}